import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { ModalFooterContext } from './Button';

// Modal — the app's single dialog primitive. react-bootstrap-compatible API backed by
// Headless UI Dialog (focus trap, ESC, body scroll lock, backdrop). Subcomponents:
// Modal.Header / Title / Body / Footer. See DESIGN.md §13 (Dialogs).
//
// Responsive behaviour (why this is structured as a bounded flex column):
//   • The panel is ALWAYS `flex-col` with a bounded max-height, so Header and Footer stay
//     pinned and only Body scrolls. Long forms therefore never push Save off-screen — the
//     single biggest mobile problem with the old centered-box modal.
//   • Phones (< sm): form modals go full-screen edge-to-edge with safe-area insets, and
//     footer buttons stack full-width (primary on top) for thumb reach. Small confirm/alert
//     dialogs (`size="sm"`) stay a centered card — full-screen for "Are you sure?" is jarring.
//   • Tablet/desktop: centered card, up to 90vh tall.
// Override the phone treatment per-modal with `mobileVariant`.
type DivProps = React.HTMLAttributes<HTMLDivElement>;

export type ModalMobileVariant = 'fullscreen' | 'center';

export interface ModalProps {
  show?: boolean;
  onHide?: () => void;
  /**
   * `3xl` is for a dialog that holds a wide table (the Edit Task dialog's 12-column Efforts
   * table, W-M8): it uses nearly the whole screen width rather than stopping at 72rem.
   */
  size?: 'sm' | 'lg' | 'xl' | '2xl' | '3xl';
  centered?: boolean;
  backdrop?: boolean | 'static';
  keyboard?: boolean;
  /** Kept for API compatibility — the panel is always a bounded scrollable column now. */
  scrollable?: boolean;
  fullscreen?: boolean | string;
  /** Phone (< sm) presentation. Defaults to 'center' for size="sm", else 'fullscreen'. */
  mobileVariant?: ModalMobileVariant;
  /**
   * Tablet presentation (W-iP10). 'fullscreen' makes the dialog a full-screen sheet on an iPad in
   * either orientation or any touch-first screen (`.wrap-modal-tablet-sheet` in tailwind.css,
   * the same query as `useTabletLayout`), for long forms that a centred card leaves with only a
   * handful of visible fields once the keyboard is up. Default: a centred card.
   */
  tabletVariant?: 'fullscreen' | 'center';
  /**
   * Opt a large modal out of the standard fixed height and let it size to its content.
   * Only affects `lg`/`xl`/`2xl` (small confirms are content-sized already).
   */
  autoHeight?: boolean;
  /**
   * When false, the page behind stays scrollable and interactive (no inert / body lock).
   * Use for long edit forms (Employees, Roles) where the table needs to stay reachable.
   */
  lockDocument?: boolean;
  className?: string;
  children?: React.ReactNode;
  dialogClassName?: string;
  contentClassName?: string;
}

const SIZE: Record<string, string> = {
  sm: 'sm:tw-max-w-sm',
  lg: 'sm:tw-max-w-2xl',
  xl: 'sm:tw-max-w-4xl',
  '2xl': 'sm:tw-max-w-6xl',
  '3xl': 'sm:tw-max-w-[min(96vw,110rem)]',
};

interface ModalType extends React.FC<ModalProps> {
  Header: React.FC<DivProps & { closeButton?: boolean; onHide?: () => void }>;
  Title: React.FC<React.HTMLAttributes<HTMLHeadingElement>>;
  Body: React.FC<DivProps>;
  Footer: React.FC<DivProps & { disabledReason?: React.ReactNode }>;
}

const HideContext = React.createContext<(() => void) | undefined>(undefined);
/** DialogTitle must sit inside a Headless UI Dialog; unlocked modals use a plain heading. */
const HeadlessDialogContext = React.createContext(true);

export const Modal = (({
  show = false, onHide, size, centered = true, backdrop = true, keyboard = true,
  scrollable, fullscreen, mobileVariant, tabletVariant = 'center', autoHeight, lockDocument = true,
  className = '', dialogClassName = '',
  contentClassName = '', children,
}: ModalProps) => {
  // Headless UI routes both Escape and backdrop clicks through one `onClose`, but the two are
  // configured separately: `keyboard` governs Escape, `backdrop: 'static'` governs the click.
  // Conflating them meant a static backdrop also swallowed Escape, leaving keyboard users with
  // no way out of the dialog.
  const closeReasonRef = React.useRef<'escape' | 'backdrop'>('backdrop');
  React.useEffect(() => {
    if (!show) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeReasonRef.current = 'escape';
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [show]);

  const handleClose = () => {
    const viaEscape = closeReasonRef.current === 'escape';
    closeReasonRef.current = 'backdrop';
    if (viaEscape ? keyboard === false : backdrop === 'static') return;
    onHide?.();
  };

  useEffect(() => {
    if (lockDocument || !show || keyboard === false) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lockDocument, show, keyboard, backdrop, onHide]);

  // Small dialogs are confirms/alerts — keep them as centered cards even on a phone.
  const phone: ModalMobileVariant = mobileVariant ?? (size === 'sm' ? 'center' : 'fullscreen');

  const widthCls = fullscreen
    ? 'tw-max-w-none tw-w-screen'
    : `tw-w-full ${SIZE[size || ''] || 'sm:tw-max-w-lg'}`;

  // Phone: full-bleed sheet (square corners) vs. inset centered card.
  const shapeCls = phone === 'fullscreen' ? 'tw-rounded-none sm:tw-rounded-xl' : 'tw-rounded-xl';

  // Standardized height: the explicitly-sized "form" modals (lg/xl/2xl) get ONE fixed height
  // so the dialog doesn't resize as content changes (switching tabs, loading rows, validation
  // messages appearing). Small/unsized confirms stay content-sized — a fixed 85vh box around
  // "Are you sure?" would look absurd. Phones are always full-height anyway.
  //
  // The fixed height starts at `lg` (1024px), not `sm` (640px). It buys stability on a desktop
  // form, but on a tablet 85vh is nearly the whole screen, so a short dialog — a confirm, a
  // small action list — rendered as a mostly-empty sheet covering the page. That is the
  // "modal windows have too much whitespace and cover the whole space" report on iPad, and the
  // whitespace around the PCR work-ID and cancel actions. Between 640px and 1024px the panel is
  // content-sized and capped at 90vh instead.
  //
  // NB: emit each height utility once per breakpoint — two competing `sm:h-*` classes would
  // resolve by stylesheet order, not by the order they appear here.
  const isLargeSize = size === 'lg' || size === 'xl' || size === '2xl' || size === '3xl';
  const fixedHeight = isLargeSize && !autoHeight;
  const phoneHeight = phone === 'fullscreen' ? 'tw-h-full tw-max-h-full' : 'tw-max-h-full';
  const heightCls = fullscreen
    ? 'tw-h-full tw-max-h-full sm:tw-h-screen sm:tw-max-h-screen'
    : fixedHeight
    ? `${phoneHeight} sm:tw-h-auto sm:tw-max-h-[90vh] lg:tw-h-[85vh] lg:tw-max-h-[85vh]`
    : `${phoneHeight} sm:tw-h-auto sm:tw-max-h-[90vh]`;

  // Outer container: no padding on a phone full-screen sheet so it truly goes edge-to-edge.
  const containerPad = phone === 'fullscreen' ? 'tw-p-0 sm:tw-p-4' : 'tw-p-3 sm:tw-p-4';

  const tabletSheet = tabletVariant === 'fullscreen';
  const panelClass = `wrap-modal-panel ${tabletSheet ? 'wrap-modal-tablet-sheet' : ''} tw-flex tw-flex-col tw-overflow-hidden tw-bg-surface tw-shadow-2xl ${widthCls} ${shapeCls} ${heightCls} ${dialogClassName} ${className} ${contentClassName}`;
  const overlayClass = `tw-fixed tw-inset-0 tw-z-[1055] tw-flex tw-justify-center ${tabletSheet ? 'wrap-modal-overlay-tablet-sheet' : ''} ${containerPad} ${
    centered ? 'tw-items-center' : 'tw-items-start'
  }`;

  if (!lockDocument) {
    if (!show) return null;
    return createPortal(
      <HeadlessDialogContext.Provider value={false}>
        <HideContext.Provider value={onHide}>
          {backdrop !== false && (
            <div
              className="wrap-modal-backdrop tw-pointer-events-none tw-fixed tw-inset-0 tw-z-[1055] tw-bg-ink/60 tw-backdrop-blur-sm"
              aria-hidden
            />
          )}
          <div className={`${overlayClass} tw-pointer-events-none`}>
            <div
              role="dialog"
              aria-modal="false"
              className={`${panelClass} tw-pointer-events-auto`}
            >
              {children}
            </div>
          </div>
        </HideContext.Provider>
      </HeadlessDialogContext.Provider>,
      document.body,
    );
  }

  return (
    <HeadlessDialogContext.Provider value={true}>
      <HideContext.Provider value={onHide}>
        <Dialog open={show} onClose={handleClose} className="tw-relative tw-z-[1055]">
          <DialogBackdrop className="wrap-modal-backdrop tw-fixed tw-inset-0 tw-bg-ink/60 tw-backdrop-blur-sm" />
          <div className={overlayClass}>
            <DialogPanel
              className={panelClass}
            >
              {children}
            </DialogPanel>
          </div>
        </Dialog>
      </HideContext.Provider>
    </HeadlessDialogContext.Provider>
  );
}) as ModalType;

// Header — pinned (never scrolls). White surface to match the app's card headers; the
// bottom rule is what separates it from the body. Extra top padding on phones clears the
// iOS status bar / notch when the sheet is full-screen.
Modal.Header = ({ closeButton, onHide, className = '', children, ...rest }) => {
  const ctxHide = React.useContext(HideContext);
  return (
    <div
      className={`tw-flex tw-shrink-0 tw-items-start tw-justify-between tw-gap-2 tw-border-b tw-border-line tw-bg-surface tw-px-4 tw-py-3 tw-pt-[max(0.75rem,env(safe-area-inset-top))] sm:tw-pt-3 ${className}`}
      {...rest}
    >
      <div className="tw-min-w-0 tw-flex-1">{children}</div>
      {closeButton && (
        <button
          type="button"
          aria-label="Close"
          onClick={() => (onHide || ctxHide)?.()}
          // 44px hit area — WCAG/iOS minimum touch target.
          className="tw--mr-1.5 tw--mt-1 tw-inline-flex tw-h-11 tw-w-11 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-md tw-text-lg tw-leading-none tw-text-content-muted tw-transition-colors hover:tw-bg-surface-hover hover:tw-text-content focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-line-focus"
        >
          <i className="bi bi-x-lg" />
        </button>
      )}
    </div>
  );
};

Modal.Title = ({ className = '', children, ...rest }) => {
  const inHeadlessDialog = React.useContext(HeadlessDialogContext);
  const titleClass = `tw-mb-0 tw-truncate tw-text-base tw-font-semibold tw-text-content sm:tw-text-lg ${className}`;
  if (inHeadlessDialog) {
    return (
      <DialogTitle as="h5" className={titleClass} {...rest}>
        {children}
      </DialogTitle>
    );
  }
  return (
    <h5 className={titleClass} {...rest}>
      {children}
    </h5>
  );
};

// Body — the only scrolling region. `min-h-0` lets it shrink inside the flex column;
// `overscroll-contain` stops iOS scroll-chaining to the page behind the modal.
Modal.Body = ({ className = '', children, ...rest }) => (
  <div className={`tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-overscroll-contain tw-p-4 ${className}`} {...rest}>
    {children}
  </div>
);

// Footer — pinned. On phones the actions stack full-width with the primary (last child in
// DOM order, e.g. Save) on top via column-reverse; from sm up it's the usual right-aligned row.
//
// Button hierarchy (W-L9): the secondary action is an outline, the primary is solid. Footers
// used a solid dark-grey Cancel next to a solid Save, so the two read as equals; inside a footer
// a `secondary` Button now renders as `outline-secondary` (see ModalFooterContext in Button), and
// a close-only footer is therefore an outline too.
//
// `disabledReason`: when the primary action is disabled, say why in the footer itself, as text a
// touch user can read (a `title` has no hover on touch). Pass it only while the action is
// disabled; it renders on the left, ahead of the buttons.
Modal.Footer = ({ className = '', children, disabledReason, ...rest }) => (
  <div
    className={`tw-flex tw-shrink-0 tw-flex-col-reverse tw-gap-2 tw-border-t tw-border-line tw-bg-surface tw-px-4 tw-py-3 tw-pb-[max(0.75rem,env(safe-area-inset-bottom))] [&>*]:tw-w-full sm:tw-flex-row sm:tw-flex-wrap sm:tw-items-center sm:tw-justify-end sm:tw-pb-3 sm:[&>*]:tw-w-auto ${className}`}
    {...rest}
  >
    <ModalFooterContext.Provider value={true}>{children}</ModalFooterContext.Provider>
    {disabledReason ? (
      <p
        data-slot="disabled-reason"
        role="status"
        className="tw-order-last tw-mb-0 tw-text-sm tw-text-content-muted sm:tw-order-first sm:tw-mr-auto sm:tw-max-w-[60%]"
      >
        {disabledReason}
      </p>
    ) : null}
  </div>
);

export default Modal;
